import { Helmet } from "react-helmet-async";
import { useEffect, useState, useRef } from "react";
// @mui
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  Popover,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import Scrollbar from "../components/scrollbar";
import { get, post, put, del } from "src/utils/networkUtils";
import AdminsTable from "src/components/admins/AdminsTable";
import { getUserPermissions } from "src/utils/permissionsUtils";
// ----------------------------------------------------------------------
const menuItems = [
  'home',
  'users',
  'user-rewards',
  'coin-transactions',
  'ticket-transactions',
  'coins-spins-pass',
  'settings',
  'codes',
  'redeem-codes',
  'promotions',
  'game-providers',
  'inbox',
  'versus',
  'user-levels',
  'lobby',
  'scoreables',
  'packs',
  'stage-periods',
  'divisions',
  'fixtures',
  'marketplace',
  'prize-draws',
  'challanges',
  'minigames'
]
export default function AdminsPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showDeleteAdminDialog, setShowDeleteAdminDialog] = useState(false);
  const [deleteAdminId, setDeleteAdminId] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    permissionLevel: "",
    password: "",
    menuItems: [],
  });
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const observerTarget = useRef(null);
  const { isSuperAdmin } = getUserPermissions();

  const limit = 5;

  const fetchAdmins = async (currentOffset, isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await get(`/admin/admins?offset=${currentOffset}&limit=${limit}`);
      const newAdmins = response.admins || [];

      if (isInitial) {
        setRows(newAdmins);
        setIsInitialLoad(false);
      } else {
        setRows(prev => [...prev, ...newAdmins]);
      }

      // If we received fewer admins than the limit, there are no more to load
      setHasMore(newAdmins.length === limit);
      setOffset(currentOffset + newAdmins.length);
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchAdmins(0, true);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    // Don't set up observer until initial load is complete
    if (isInitialLoad) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchAdmins(offset);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [offset, hasMore, loadingMore, loading, isInitialLoad]);

  const handleOpenDialog = () => {
    setNewAdmin({ username: "", permissionLevel: 0, password: "", menuItems: [] });
    setIsEditMode(false);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (admin) => {
    setNewAdmin({
      id: admin.adminId,
      username: admin.username,
      permissionLevel: admin.permissionLevel,
      password: "", // Keep password empty for security
      menuItems: admin.menuItems || [],
    });
    setIsEditMode(true);
    setDialogOpen(true);
    setOpen(null); // Close the popover when opening edit dialog
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditMode(false);
  };

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const openEditAdminFromMenu = (id) => {
    if (id && rows) {
      const admin = rows.find((row) => +row.adminId === +id);
      if (admin) {
        handleOpenEditDialog(admin);
      }
    }
  };

  const openDeleteAdminDialog = (id) => {
    setDeleteAdminId(id);
    setShowDeleteAdminDialog(true);
    setOpen(null);
  };

  const deleteAdminHandler = (id) => {
    setLoading(true);
    del(`/admin/admins/${id}`).then(() => {
      setShowDeleteAdminDialog(false);
      window.location.reload();
    });
  };

  const handleChange = (e) => {
    setNewAdmin({ ...newAdmin, [e.target.name]: e.target.value });
  };

  const handleMenuItemChange = (menuItem) => {
    const currentMenuItems = newAdmin.menuItems || [];
    const updatedMenuItems = currentMenuItems.includes(menuItem)
      ? currentMenuItems.filter(item => item !== menuItem)
      : [...currentMenuItems, menuItem];

    setNewAdmin({ ...newAdmin, menuItems: updatedMenuItems });
  };

  const handleAddAdmin = async () => {
    if (isEditMode) {
      // Edit existing admin
      const updateData = { ...newAdmin };
      // Remove password if it's empty (don't update password)
      if (!updateData.password) {
        delete updateData.password;
      }
      put(`/admin/admins/${newAdmin.id}`, updateData).then(function (response) {
        setDialogOpen(false);
        window.location.reload();
      });
    } else {
      // Add new admin
      post(`/admin/admins`, newAdmin).then(function (response) {
        setDialogOpen(false);
        window.location.reload();
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Admins </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Admins
          </Typography>
          {isSuperAdmin && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenDialog}
            >
              + Add Admin
            </Button>
          )}
        </Stack>

        <Card>
          {loading && rows.length === 0 ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Scrollbar>
                <AdminsTable
                  admins={rows}
                  onEditAdmin={handleOpenEditDialog}
                  onMenuOpen={handleOpenMenu}
                />
              </Scrollbar>

              {/* Infinite scroll trigger */}
              <div ref={observerTarget} style={{ height: '20px' }} />

              {/* Loading indicator for pagination */}
              {loadingMore && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    padding: 2
                  }}
                >
                  <CircularProgress size={30} />
                </Box>
              )}
            </>
          )}
        </Card>

        {/* Add Admin Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{isEditMode ? 'Edit Admin' : 'Add Admin'}</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              <TextField
                name="username"
                label="Username"
                value={newAdmin.username}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
              <FormControl fullWidth variant="outlined">
                <InputLabel>Permission Level</InputLabel>
                <Select
                  name="permissionLevel"
                  value={newAdmin.permissionLevel}
                  onChange={handleChange}
                  label="Permission Level"
                >
                  <MenuItem value={0}>Superadmin</MenuItem>
                  <MenuItem value={1}>Admin</MenuItem>
                  <MenuItem value={2}>Editor</MenuItem>
                  <MenuItem value={3}>Viewer</MenuItem>
                  <MenuItem value={4}>Client</MenuItem>
                </Select>
              </FormControl>
              <TextField
                name="password"
                label="Password"
                type="password"
                value={newAdmin.password}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                placeholder={isEditMode ? "Leave blank to keep current password" : ""}
              />
              {newAdmin.permissionLevel === 4 && (
                <FormControl component="fieldset" variant="outlined">
                  <FormLabel component="legend">Menu Items</FormLabel>
                  <Grid container spacing={1}>
                    {menuItems.map((item) => (
                      <Grid item xs={4} key={item}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newAdmin.menuItems.includes(item)}
                              onChange={() => handleMenuItemChange(item)}
                              name={item}
                            />
                          }
                          label={item.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormControl>
              )}

            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleAddAdmin} variant="contained">
              {isEditMode ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteAdminDialog}
          onClose={() => setShowDeleteAdminDialog(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Delete Admin</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this admin? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteAdminDialog(false)}>Cancel</Button>
            <Button
              onClick={() => deleteAdminHandler(deleteAdminId)}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Three dots popover menu */}
        <Popover
          open={Boolean(open)}
          anchorEl={open}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              p: 1,
              width: 160,
              "& .MuiMenuItem-root": {
                px: 1,
                typography: "body2",
                borderRadius: 0.75,
              },
            },
          }}
        >
          <MenuItem onClick={() => openEditAdminFromMenu(openId)} sx={{ color: "text.secondary" }}>
            <EditIcon sx={{ mr: 2 }} />
            Edit
          </MenuItem>

          {isSuperAdmin && (
            <MenuItem onClick={() => openDeleteAdminDialog(openId)} sx={{ color: "error.main" }}>
              <DeleteIcon sx={{ mr: 2 }} />
              Delete
            </MenuItem>
          )}
        </Popover>
      </Container>
    </>
  );
}
